import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Users,
  Calendar,
  Video,
  Edit,
  Target,
  Activity,
  BriefcaseBusiness,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useNavigate, Link, useLocation } from "react-router-dom";
import NotificationsPanel from "./NotificationsPanel";
import PushNotificationPrompt from "./PushNotificationPrompt";

export default function Layout({ children }) {
  const { user, permissions, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: LayoutDashboard,
      roles: ["owner", "pillar", "employee"],
    },
    { name: "Leads", href: "/leads", icon: Users, roles: ["owner", "pillar"] },
    {
      name: "Meetings",
      href: "/meetings",
      icon: Calendar,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Clients",
      href: "/clients",
      icon: Users,
      roles: ["owner", "pillar"],
    },
    {
      name: "Shoots",
      href: "/shoots",
      icon: Video,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Editing",
      href: "/editing",
      icon: Edit,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Ads",
      href: "/ads",
      icon: Target,
      roles: ["owner", "pillar", "employee"],
    },

    {
      name: "Social Media",
      href: "/social",
      icon: Users,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Content Calendar",
      href: "/calendar",
      icon: Calendar,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Task Management",
      href: "/work",
      icon: BriefcaseBusiness,
      roles: ["owner", "pillar", "employee"],
    },
    {
      name: "Activity Logs",
      href: "/activity",
      icon: Activity,
      roles: ["owner"],
    },
    { name: "Users & Team", href: "/team", icon: Users, roles: ["owner", "pillar"] },
    { name: "Roles & Permissions", href: "/roles", icon: ShieldCheck, roles: ["owner"] },
    {
      name: "System Settings",
      href: "/settings",
      icon: Menu,
      roles: ["owner"],
    },
  ];

  const filteredNav = navigation.filter((item) => {
    const pageKey = item.href.slice(1);
    // Owners retain the full navigation. All other roles are driven by the
    // permissions returned by the backend, including custom roles such as
    // social_media_manager and graphic_designer.
    if (user?.role === "owner") return true;
    if (permissions.length > 0) return permissions.includes(pageKey);
    return item.roles.includes(user?.role);
  });

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-border bg-sidebar transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ backgroundColor: "#f8fafc" }}
      >
        <div className="p-6 border-b border-sidebar-border flex items-center justify-between">
          <h1
            className="text-2xl font-bold text-primary"
            style={{ letterSpacing: "-0.02em" }}
          >
            AgencyOS
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <nav className="p-4 space-y-1">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(" ", "-")}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-border bg-white flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold lg:hidden">AgencyOS</h2>
          </div>

          <div className="flex items-center gap-2 lg:gap-4">
            <PushNotificationPrompt />
            <NotificationsPanel />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2"
                  data-testid="user-menu-button"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {user?.name?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {user?.role}
                    </p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
